import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import findElementByIdDraggable from "../helpers/findElementByIdDraggable";
import { DraggableComponentTypes } from "../types/draggable";
import useEditorAi from "./useEditorAi";
const EditorContext = createContext();
export const useEditor = () => useContext(EditorContext);
function EditorProvider({ children, page, initMode = "editor", backendApi = "", elements = [], }) {
    const [content, setContent] = useState(page?.data?.content ? JSON.parse(page?.data?.content.content) : []);
    const [mode, setMode] = useState(initMode);
    const [editorDevice, setEditorDevice] = useState("desktop");
    const [editorScale, setEditorScale] = useState(1);
    const [editorStylingDevice, setEditorStylingDevice] = useState("all");
    const [selectedElement, setSelectedElement] = useState(null);
    const [elementModalOpen, setElementModalOpen] = useState(false);
    const [elementAddDragActive, setElementAddDragActive] = useState(false);
    const [elementOverviewOpen, setElementOverviewOpen] = useState(false);
    const [elementType, setElementType] = useState(null);
    const [editableItem, setEditableItem] = useState(null);
    const [hoveredElement, setHoveredElement] = useState(null);
    const [hasUpdate, setHasUpdate] = useState(false);
    const [iframeMount, setIframeMount] = useState(null);
    const [lastSelectedEditableTime, setLastSelectedEditableTime] = useState(0);
    const dragOverComponent = useRef(null);
    const [draggableHtmlEditor, setDraggableHtmlEditor] = useState(null);
    const [dragElementMenu, setDragElementMenu] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const { activeItem: aiActiveItem, setActiveItem: setAiActiveItem } = useEditorAi();
    const addElement = (element, index) => {
        if (index >= -1) {
            const contentArray = [...content];
            contentArray.splice(index === -1 ? 0 : index, 0, element);
            setContent(contentArray);
        }
        else {
            setContent([...content, element]);
        }
        setElementAddDragActive(false);
        setHasUpdate(true);
        setElementModalOpen(false);
    };
    const changeElementPostion = (elementId, action) => {
        const contentArray = [...content];
        const element = content.find((c) => c.id === elementId);
        const elementIndex = content.findIndex((c) => c.id === elementId);
        contentArray.splice(elementIndex, 1);
        if (action === "up")
            contentArray.splice(elementIndex - 1, 0, element);
        if (action === "down")
            contentArray.splice(elementIndex + 1, 0, element);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const duplicateElement = (elementId) => {
        const contentArray = [...content];
        const element = content.find((c) => c.id === elementId);
        const elementIndex = content.findIndex((c) => c.id === elementId);
        const duplatedElement = {
            ...element,
            id: uuidv4(),
        };
        contentArray.splice(elementIndex + 1, 0, duplatedElement);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const removeElement = (elementId) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        contentArray.splice(elementIndex, 1);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableAddComponent = (itemId, componentType, elementId, data = null, index = null, dragFromElementId = null) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        if (elementIndex === -1)
            return;
        const selectedElement = findElementByIdDraggable(itemId, contentArray[elementIndex]?.props);
        if (data && data.id) {
            // move existing item
            const currentChilds = [...(selectedElement?.children || [])];
            // items same parent
            if (data.id.split("/").slice(0, -1).join("/") === itemId) {
                const indexOfCurrentElement = selectedElement?.children?.findIndex((c) => c.id === data?.id);
                let elementToMove = currentChilds.splice(indexOfCurrentElement, 1)[0];
                currentChilds.splice(index || currentChilds.length, 0, elementToMove);
            }
            else {
                // items different parent
                const differentParentElementIndex = content.findIndex((c) => c.id === dragFromElementId);
                const differentParentElement = findElementByIdDraggable(data.id.split("/").slice(0, -1).join("/"), contentArray[differentParentElementIndex]?.props);
                const newChildId = `${itemId}/${uuidv4()}`;
                currentChilds.splice(index || currentChilds.length, 0, {
                    ...data,
                    id: newChildId,
                    children: JSON.parse(JSON.stringify(data.children || []).replaceAll(newChildId, data.id)),
                });
                differentParentElement?.children.splice(differentParentElement?.children.findIndex((c) => c.id === data.id), 1);
            }
            selectedElement.children = currentChilds;
        }
        else {
            // add new item
            // create props if not exist
            if (!contentArray[elementIndex].props) {
                contentArray[elementIndex] = {
                    ...contentArray[elementIndex],
                    props: {},
                };
            }
            //create children if not exist
            if (!contentArray[elementIndex].props?.children) {
                contentArray[elementIndex].props = {
                    ...contentArray[elementIndex].props,
                    id: 1,
                    children: [],
                };
            }
            const draggableComponentId = `${itemId}/${uuidv4()}`;
            const draggableComponent = {
                id: draggableComponentId,
                type: componentType,
                ...(data?.props && { props: data.props }),
                ...(data?.children && {
                    children: JSON.parse(JSON.stringify(data.children).replaceAll("elementId", draggableComponentId)),
                }),
            };
            if (itemId) {
                const parentElement = findElementByIdDraggable(itemId, contentArray[elementIndex]?.props);
                if (!parentElement.children) {
                    parentElement.children = [];
                }
                parentElement.children.splice(index || parentElement.children.length, 0, draggableComponent);
            }
        }
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableUpdateElementProp = (value, id = null) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === selectedElement);
        if (elementIndex === -1)
            return;
        const editableItemKey = id || editableItem?.getAttribute("editor-key");
        const editableElement = findElementByIdDraggable(editableItemKey, contentArray[elementIndex]?.props);
        if (!editableElement?.props) {
            editableElement.props = {};
        }
        editableElement.props = {
            ...editableElement?.props,
            ...value,
        };
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableFindElement = (editableItemKey, data) => {
        const contentObj = data || content;
        const elementIndex = contentObj.findIndex((c) => c.id === selectedElement);
        if (elementIndex === -1)
            return;
        return findElementByIdDraggable(editableItemKey, contentObj[elementIndex]?.props);
    };
    const draggableItemUpdate = (editableItemKey, action, elementId, updatedContent = null) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        if (elementIndex === -1)
            return;
        const currentParentElement = findElementByIdDraggable(editableItemKey.split("/").slice(0, -1).join("/"), contentArray[elementIndex]?.props) || contentArray[elementIndex]?.props;
        if (!currentParentElement)
            return;
        const indexOfCurrentElement = currentParentElement?.children.findIndex((c) => c.id === editableItemKey);
        const currentChilds = currentParentElement?.children
            ? [...currentParentElement?.children]
            : [];
        if (action === "remove") {
            if (editableItem?.getAttribute("editor-key") === editableItemKey) {
                setEditableItem(null);
            }
            currentChilds.splice(indexOfCurrentElement, 1);
        }
        if (action === "duplicate") {
            const duplicatedElement = {
                ...currentParentElement?.children[indexOfCurrentElement],
                id: `${currentParentElement.id}/${uuidv4()}`,
            };
            currentChilds.splice(indexOfCurrentElement + 1, 0, JSON.parse(JSON.stringify(duplicatedElement).replaceAll(currentParentElement?.children[indexOfCurrentElement].id, duplicatedElement.id)));
        }
        if (["up", "down"].includes(action)) {
            let elementToMove = currentChilds.splice(indexOfCurrentElement, 1)[0];
            currentChilds.splice(action === "up" ? indexOfCurrentElement - 1 : indexOfCurrentElement + 1, 0, elementToMove);
        }
        if (action === "changeToIcon") {
            const updatedElement = {
                ...currentParentElement?.children[indexOfCurrentElement],
                type: DraggableComponentTypes.Icon,
            };
            currentChilds[indexOfCurrentElement] = updatedElement;
        }
        if (action === "wrapInDiv") {
            if (editableItem?.getAttribute("editor-key") === editableItemKey) {
                setEditableItem(null);
            }
            const newParentId = `${currentParentElement.id}/${uuidv4()}`;
            const updatedElement = {
                type: DraggableComponentTypes.Div,
                id: newParentId,
                children: [currentChilds[indexOfCurrentElement]].map((i) => {
                    const newId = `${newParentId}/${uuidv4()}`;
                    return {
                        ...i,
                        id: newId,
                        children: i?.children
                            ? JSON.parse(JSON.stringify(i.children).replaceAll(i.id, newId))
                            : [],
                    };
                }),
            };
            currentChilds[indexOfCurrentElement] = updatedElement;
        }
        if (action === "updateContent") {
            currentChilds[indexOfCurrentElement] = updatedContent;
        }
        currentParentElement.children = currentChilds;
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableGridModify = (editableItemKey, action, actionValue = {}) => {
        const contentArray = [...content];
        const gridItem = draggableFindElement(editableItemKey, contentArray);
        if (action === "add") {
            gridItem.children = [
                ...gridItem.children,
                {
                    id: `${editableItemKey}/${uuidv4()}`,
                    type: DraggableComponentTypes.GridItem,
                },
            ];
        }
        if (action === "remove") {
            gridItem.children = gridItem.children.filter((c) => c.id !== actionValue.id);
        }
        if (action === "update") {
            gridItem.props = {
                ...gridItem.props,
                ...actionValue,
            };
        }
        if (action === "copy") {
            const element = gridItem.children.find((c) => c.id === actionValue.id);
            let newElement = {
                ...element,
                id: `${editableItemKey}/${uuidv4()}`,
                type: DraggableComponentTypes.GridItem,
            };
            newElement = JSON.parse(JSON.stringify(element).replaceAll(element.id, `${editableItemKey}/${uuidv4()}`));
            gridItem.children = [...gridItem.children, newElement];
        }
        setContent(contentArray);
        setHasUpdate(true);
    };
    return (_jsx(EditorContext.Provider, { value: {
            elements,
            content,
            addElement,
            selectedElement,
            setSelectedElement,
            changeElementPostion,
            removeElement,
            elementModalOpen,
            setElementModalOpen,
            elementAddDragActive,
            setElementAddDragActive,
            mode,
            setMode,
            editorDevice,
            setEditorDevice,
            editorScale,
            setEditorScale,
            editorStylingDevice,
            setEditorStylingDevice,
            editableItem,
            setEditableItem,
            backendApi,
            hasUpdate,
            page,
            setHasUpdate,
            elementType,
            setElementType,
            iframeMount,
            setIframeMount,
            lastSelectedEditableTime,
            setLastSelectedEditableTime,
            draggableAddComponent,
            draggableUpdateElementProp,
            draggableFindElement,
            draggableItemUpdate,
            draggableGridModify,
            dragOverComponent,
            dragElementMenu,
            setDragElementMenu,
            elementOverviewOpen,
            setElementOverviewOpen,
            draggableHtmlEditor,
            setDraggableHtmlEditor,
            dragActive,
            setDragActive,
            setContent,
            hoveredElement,
            setHoveredElement,
            aiActiveItem,
            setAiActiveItem,
        }, children: children }));
}
export default EditorProvider;
//# sourceMappingURL=EditorProvider.js.map